import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { InvoiceDetail } from '@/components/invoices/InvoiceDetail';
import { useCompaniesApi } from '@/services/api';
import { t } from '@/i18n';
import type { InvoiceDirection } from '@ip/shared-types';

export function InvoiceDetailPage() {
  const { nip, invoiceId } = useParams<{ nip: string; invoiceId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const rawListSearch = (location.state as { listSearch?: string })?.listSearch || '';
  const listSearch = rawListSearch.startsWith('?') || rawListSearch === '' ? rawListSearch : '';
  // Optional explicit origin (e.g. 'sales/report') — lets sub-views of a
  // section get their own "back" target; only known list paths are honored.
  const rawListPath = (location.state as { listPath?: string })?.listPath;
  const stateListPath = rawListPath === 'sales/report' ? rawListPath : undefined;
  const [companyName, setCompanyName] = useState('');
  const { getCompanyDetail } = useCompaniesApi();

  useEffect(() => {
    if (!nip) return;
    const currentNip = nip;
    let mounted = true;
    async function loadName() {
      if (currentNip === 'other') {
        if (mounted) setCompanyName(t('navigation.unassigned'));
        return;
      }
      try {
        const detail = await getCompanyDetail(currentNip);
        if (mounted && detail.name) setCompanyName(detail.name);
      } catch {
        // ignore
      }
    }
    loadName();
    return () => { mounted = false; };
  }, [nip, getCompanyDetail]);

  // Sale invoices live in the Sprzedaż (sales) list; everything else (default/
  // undefined direction) belongs to the Koszty (purchase/invoices) list.
  const handleClose = useCallback((direction?: InvoiceDirection) => {
    const listPath = stateListPath ?? (direction === 'sale' ? 'sales' : 'invoices');
    navigate(`/${nip}/${listPath}${listSearch}`);
  }, [nip, navigate, listSearch, stateListPath]);

  const handleSaved = useCallback((direction?: InvoiceDirection) => {
    const listPath = stateListPath ?? (direction === 'sale' ? 'sales' : 'invoices');
    navigate(`/${nip}/${listPath}${listSearch}`, { state: { refetch: true } });
  }, [nip, navigate, listSearch, stateListPath]);

  if (!nip || !invoiceId) return null;

  return (
    <InvoiceDetail
      companyNip={nip}
      companyName={companyName}
      invoiceId={invoiceId}
      onClose={handleClose}
      onSaved={handleSaved}
    />
  );
}
